import { TabContext, TabPanel } from '@mui/lab'
import { Box, Stack, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Holidays from 'date-holidays'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import SingleFilterPopup from '../../components/Popup/SingleFilterPopup'
import Searchbar from '../../components/Searchbar/Searchbar'
import defaultProps from '../../config/defaultProps'
import { useAppDispatch, useAppSelector } from '../../store/redux/hooks'
import { getCurrentUser, getIsManagement } from '../../store/redux/reducers/currentUserSlice'
import { useGetDictionariesQuery } from '../../store/redux/reducers/dictionarySlice'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { RequestProps, Settlement } from '../../store/types'
import DateFormat from '../../store/types/enums/DateFormat'
import { StyledTab } from '../AccessibilitySettingsView/AccessibilitySettingsView'
import CompletedTable from './CompletedTable'
import PlanTable from './PlanTable'

const SettlementsView = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [tabIndex, setTabIndex] = useState('Plan')
  const currentUser = useSelector(getCurrentUser)
  const isManagement = useSelector(getIsManagement)
  const [searchText, setSearchText] = useState('')
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  const { from, to } = useAppSelector(state => state.projectDistributionDateRange)
  const { leaderId } = useAppSelector(state => state.leaderId)
  const hd = new Holidays('PL', '', '', { types: ['public'] })

  const getWorkHoursForMonth = (year: number, month: number) => {
    const holidays = hd.getHolidays().filter(h => h.type === 'public' && dayjs(h.date).month() === month)
    const holidayDates = holidays.map(d => dayjs(d.date))

    const businessDays = dayjs(`${year}-${month + 1}-01`)
      .businessDaysInMonth()
      .filter(d => holidayDates.find(hd => hd.isSame(d)) === undefined)

    return businessDays.length * 8
  }

  const getTotalWorkHours = (start: Dayjs, end: Dayjs) => {
    let totalWorkHours = 0
    let current = start
    let monthlyHours = []

    while (current.isBefore(end) || current.isSame(end, 'month')) {
      const year = current.year()
      const month = current.month()

      const workHoursForMonth = getWorkHoursForMonth(year, month)
      totalWorkHours += workHoursForMonth

      monthlyHours.push({ month: current.format(DateFormat.YearMonth), hours: workHoursForMonth })

      current = current.add(1, 'month')
    }

    return { totalWorkHours, monthlyHours }
  }

  const { totalWorkHours, monthlyHours } = getTotalWorkHours(dayjs(from, 'YYYY-MM'), dayjs(to, 'YYYY-MM'))

  useEffect(() => {
    dispatch(setPageName(t('page.name.settlements')))
    dispatch(setPageIndex(PageIndex.SETTLEMENTS))

    dispatch(
      pageHeaderActions.setButtons({
        monthPicker: false,
        monthRangePicker: { shown: true, disablePaginationDefaultForwardMonths: true },
        leaderPicker: { shown: isManagement },
      })
    )

    return () => {
      dispatch(pageHeaderActions.setInitialState())
    }
  }, [dispatch, t, isManagement])

  const handleSearch = (value: string) => {
    setSearchText(value)
  }

  const [requestProps, setRequestProps] = useState<RequestProps>(defaultProps)
  const { data: options } = useGetDictionariesQuery({
    page: 0,
    size: 20,
    group: 'EMPLOYMENT_TYPE',
  })

  const renderFooter = (settlements: Settlement[]) => {
    const sums = settlements.reduce(
      (acc, settlement) => {
        acc.employmentPercentage += settlement.employmentPercentage
        acc.totalHoursInGivenPeriod += settlement.totalHoursInGivenPeriod
        acc.totalHoursWorkedOrPlanned += settlement.totalHoursWorkedOrPlanned
        acc.totalOperatingHours += settlement.totalOperatingHours
        acc.totalNonOperatingHours += settlement.totalNonOperatingHours
        acc.totalMissingHours += settlement.totalMissingOrFreeHours
        acc.percentageHoursWorked += settlement.percentageHoursWorkedOrPlanned
        acc.percentageOperatingHours += settlement.percentageOperatingHours
        return acc
      },
      {
        employmentPercentage: 0,
        totalHoursInGivenPeriod: 0,
        totalHoursWorkedOrPlanned: 0,
        totalOperatingHours: 0,
        totalNonOperatingHours: 0,
        totalMissingHours: 0,
        percentageHoursWorked: 0,
        percentageOperatingHours: 0,
      }
    )

    const footerCellStyles = {
      px: 1,
      py: 0.5,
      fontSize: '0.8rem',
      fontWeight: 700,
    }

    return (
      <TableRow>
        <TableCell sx={{ py: 0.5 }}></TableCell>
        <TableCell
          align={'left'}
          sx={{
            px: 1,
            py: 1,
            pl: 0,
          }}
        >
          <Typography sx={{ whiteSpace: 'nowrap', fontWeight: 700, fontSize: '0.8rem' }}>
            {t('page.settlements.table.footer.sum')}
          </Typography>
        </TableCell>
        <TableCell id={'fte-sum'} align={'center'} sx={footerCellStyles}>
          {sums.employmentPercentage.toFixed(1)}
        </TableCell>
        <TableCell id={'given-period-sum'} align={'center'} sx={footerCellStyles}>
          {sums.totalHoursInGivenPeriod.toFixed(1)}
        </TableCell>
        <TableCell id={'worked-hours-sum'} align={'center'} sx={footerCellStyles}>
          {sums.totalHoursWorkedOrPlanned.toFixed(1)}
        </TableCell>
        <TableCell id={'operating-hours-sum'} align={'center'} sx={footerCellStyles}>
          {sums.totalOperatingHours.toFixed(1)}
        </TableCell>
        <TableCell id={'non-operating-hours-sum'} align={'center'} sx={footerCellStyles}>
          {sums.totalNonOperatingHours.toFixed(1)}
        </TableCell>
        <TableCell align={'center'} sx={footerCellStyles}>
          {sums.totalMissingHours.toFixed(1)}
        </TableCell>
        <TableCell id={'%-hours-worked-sum'} align={'center'} sx={footerCellStyles}>
          {sums.percentageHoursWorked.toFixed(1)}
        </TableCell>
        <TableCell id={'%-operating-hours-sum'} align={'center'} sx={footerCellStyles}>
          {sums.percentageOperatingHours.toFixed(1)}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <Stack sx={{ px: isSm ? 0 : 3, paddingBottom: 3 }}>
      <TabContext value={tabIndex}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isSm ? 'column' : 'row',
            alignItems: isSm ? 'auto' : 'center',
            justifyContent: 'space-between',
          }}
        >
          <Tabs value={tabIndex} onChange={(_, newValue) => setTabIndex(newValue)} sx={{ pb: isSm ? 1.5 : 0 }}>
            <StyledTab id={'tab-plan'} value={'Plan'} label={t('page.settlements.tabs.plan')} sx={{ width: '50%' }} />
            <StyledTab
              id={'tab-completed'}
              value={'Completed'}
              label={t('page.settlements.tabs.completed')}
              sx={{ width: '50%' }}
            />
          </Tabs>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              ml: !isSm ? 'auto' : 0,
              py: 1.5,
            }}
          >
            <Typography component="h2" variant="subtitle2" fontWeight={500}>
              {t('page.settlements.totalWorkHours')}
            </Typography>
            {!totalWorkHours ? (
              <Typography component="h3" variant="h5">
                -
              </Typography>
            ) : (
              <Typography component="h3" variant="h5">
                {totalWorkHours || 0}
              </Typography>
            )}
          </Box>
        </Box>

        <Stack direction={isSm ? 'column' : 'row'} gap={2} sx={{ pt: 1.5 }}>
          <Searchbar placeholder={t('components.placeholder.search')} handleChange={handleSearch} />
          <SingleFilterPopup
            singleFilterButtonText={t('page.settlements.filter.employmentTypes')}
            singleFilterHeaderText={t('page.settlements.filter.employmentTypes')}
            popupId={'filter-employment-type'}
            setRequestProps={setRequestProps}
            dynamicPropName={'employmentType'}
            optionsToChoose={options?.content.map(option => ({ value: option.value, label: option.value }))}
            notFoundText={t('page.settlements.filter.employmentTypesNotFound')}
          />
        </Stack>
        <TabPanel value={'Plan'} sx={{ px: 0, paddingBottom: 0 }} aria-labelledby={'tab-plan'}>
          <PlanTable
            from={from}
            to={to}
            leaderId={leaderId || currentUser!!.id}
            monthlyHours={monthlyHours}
            renderFooter={renderFooter}
            searchText={searchText}
            employmentTypeFilter={requestProps.employmentType}
          />
        </TabPanel>
        <TabPanel value={'Completed'} sx={{ px: 0 }} aria-labelledby={'tab-completed'}>
          <CompletedTable
            workHoursInGivenPeriod={totalWorkHours}
            leaderId={leaderId || currentUser!!.id}
            from={from}
            to={to}
            renderFooter={renderFooter}
            searchText={searchText}
            employmentTypeFilter={requestProps.employmentType}
          />
        </TabPanel>
      </TabContext>
    </Stack>
  )
}

export default SettlementsView
